import { Stack } from "@similarweb/ui-components/foundations";

import type { SignalId, SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import AddSignalButton from "app/pages/sales-intelligence/sub-modules/signals/components/buttons/AddSignalButton/AddSignalButton";
import SignalView from "app/pages/sales-intelligence/sub-modules/signals/components/shared/Signal/Signal";
import SignalWithActionsView from "app/pages/sales-intelligence/sub-modules/signals/components/shared/Signal/SignalWithActions/SignalWithActions";
import type SignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalFilters";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";

export type SignalsListItemsProps = {
    signals: readonly UISignal<SignalFilters>[];
    selectedSignalType: SignalType;
    isUserCustomizationAllowed: boolean;
    highlightedSignalId?: SignalId | null;
    onSignalCreate(signalType: SignalType): void;
    onSignalDelete<F extends SignalFilters>(signal: Readonly<UISignal<F>>): void;
    onSignalEdit<F extends SignalFilters>(signal: Readonly<UISignal<F>>): void;
    areActionsEnabled?: boolean;
    isCreateAndEditEnabled?: boolean;
};

const SignalsListItems = (props: SignalsListItemsProps) => {
    const {
        signals,
        selectedSignalType,
        highlightedSignalId,
        onSignalCreate,
        onSignalDelete,
        onSignalEdit,
        isUserCustomizationAllowed,
        areActionsEnabled = true,
        isCreateAndEditEnabled = true,
    } = props;

    const handleAddSignalClick = (): void => {
        onSignalCreate(selectedSignalType);
    };

    return (
        <Stack
            gap="12px"
            width="100%"
            data-automation="signals-list-items"
            alignItems="flex-start"
        >
            <Stack
                gap="12px"
                width="100%"
            >
                {signals.map((signal) => {
                    const isAdminSignalLocked = !isUserCustomizationAllowed && Boolean(signal.accountSignalId);
                    const shouldShowActions = areActionsEnabled && !isAdminSignalLocked;
                    const isHighlighted = signal.id === highlightedSignalId;

                    return shouldShowActions ? (
                        <SignalWithActionsView
                            key={signal.id}
                            uiSignal={signal}
                            isHighlighted={isHighlighted}
                            isEditEnabled={isCreateAndEditEnabled}
                            onEditClick={onSignalEdit}
                            onDeleteClick={onSignalDelete}
                        />
                    ) : (
                        <SignalView
                            key={signal.id}
                            uiSignal={signal}
                            isHighlighted={isHighlighted}
                        />
                    );
                })}
            </Stack>
            {areActionsEnabled && isCreateAndEditEnabled ? (
                <AddSignalButton
                    signalType={selectedSignalType}
                    onClick={handleAddSignalClick}
                />
            ) : null}
        </Stack>
    );
};

export default SignalsListItems;
